import { useState } from 'react';
import axios from 'axios';
import {
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DualFileUpload from '../components/DualFileUpload';
import ReviewViewer from '../components/ReviewViewer';
import { API_ENDPOINTS } from '../config/api';

interface FileData {
  rfpFilename: string;
  proposalFilename: string;
}

interface ComplianceCheck {
  passed: boolean;
  requiredDocuments: Array<{
    name: string;
    mandatory: boolean;
    present: boolean;
    location?: string;
  }>;
  missingMandatory: string[];
  missingOptional: string[];
}

interface CompletenessAnalysis {
  score: number;
  totalRequirements: number;
  addressedRequirements: number;
  partiallyAddressed: number;
  notAddressed: number;
  gaps: Array<{
    requirement: string;
    issue: string;
    severity: 'high' | 'medium' | 'low';
  }>;
}

interface OrganizationalAlignment {
  score: number;
  evidencedClaims: number;
  unsupportedClaims: number;
  contextDocumentsUsed: string[];
  alignmentIssues: Array<{
    claim: string;
    issue: string;
    suggestion: string;
  }>;
}

interface QualityAssessment {
  overallScore: number;
  relevanceScore: number;
  clarityScore: number;
  innovationScore: number;
  feasibilityScore: number;
  strengthAreas: string[];
  improvementAreas: string[];
}

interface InlineComment {
  section: string;
  text: string;
  comment: string;
  type: 'strength' | 'weakness' | 'suggestion' | 'missing' | 'question';
  priority: 'high' | 'medium' | 'low';
  category: 'compliance' | 'completeness' | 'alignment' | 'quality';
}

interface ReviewData {
  reviewId: string;
  overallScore: number;
  summary: string;
  strengths: string[];
  weaknesses: string[];
  suggestions: string[];
  compliance: ComplianceCheck;
  completeness: CompletenessAnalysis;
  organizationalAlignment: OrganizationalAlignment;
  quality: QualityAssessment;
  inlineComments: InlineComment[];
  timestamp: string;
}

export default function Review() {
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFilesUploaded = async (rfpFile: File, proposalFile: File) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('rfp', rfpFile);
      formData.append('proposal', proposalFile);

      const response = await axios.post(API_ENDPOINTS.review.submit, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      setFileData({
        rfpFilename: rfpFile.name,
        proposalFilename: proposalFile.name
      });
      setReviewData(response.data);
    } catch (err) {
      console.error('Review failed:', err);
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.error || 'Failed to review proposal');
      } else {
        setError('Failed to review proposal. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFileData(null);
    setReviewData(null);
    setError(null);
  };

  const ReviewDetailsPanel = () => (
    <Card elevation={2}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" component="h2" gutterBottom fontWeight={600}>
              Review Details
            </Typography>
            <Stack spacing={1.5} mt={2}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <AssignmentIcon color="primary" sx={{ mt: 0.5, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      RFP Document
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fileData?.rfpFilename}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <DescriptionIcon color="primary" sx={{ mt: 0.5, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Proposal Document
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fileData?.proposalFilename}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {reviewData && (
            <>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Overall Score
                </Typography>
                <Typography variant="h3" color="primary" mt={1} fontWeight={700}>
                  {reviewData.overallScore}/100
                </Typography>
                <Chip
                  label={reviewData.compliance.passed ? 'Compliant' : 'Non-Compliant'}
                  color={reviewData.compliance.passed ? 'success' : 'error'}
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Evaluation Scores
                </Typography>
                <Stack spacing={1} mt={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Completeness
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {reviewData.completeness.score}/100
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Organizational Alignment
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {reviewData.organizationalAlignment.score}/100
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Quality
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {reviewData.quality.overallScore}/100
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </>
          )}

          <Button
            variant="outlined"
            size="medium"
            fullWidth
            onClick={handleReset}
            sx={{ mt: 2 }}
          >
            Review New Documents
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!reviewData && !loading ? (
        <DualFileUpload onFilesUploaded={handleFilesUploaded} />
      ) : loading ? (
        <Box textAlign="center" py={6}>
          <CircularProgress size={56} thickness={4} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Analyzing Your Proposal
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Running comprehensive four-tier evaluation...
          </Typography>

          {/* Progress indicators */}
          <Stack spacing={1} alignItems="center" sx={{ mt: 3, maxWidth: 400, mx: 'auto' }}>
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2">Compliance Check</Typography>
            </Box>
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Completeness Analysis</Typography>
            </Box>
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, opacity: 0.5 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: 2, borderColor: 'divider' }} />
              <Typography variant="body2">Organizational Alignment</Typography>
            </Box>
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, opacity: 0.5 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: 2, borderColor: 'divider' }} />
              <Typography variant="body2">Quality Assessment</Typography>
            </Box>
          </Stack>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <ReviewDetailsPanel />
          </Grid>

          <Grid size={{ xs: 12, md: 8, lg: 9 }}>
            <Stack spacing={3}>
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {reviewData && (
                <ReviewViewer reviewData={reviewData} />
              )}
            </Stack>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
